import { useParams } from "react-router";
import { pick } from "es-toolkit";
import { useShowFilters } from "src/components/features/medium/AssetsFilter/AssetsFilters.hooks";
import type { TimeWindow } from "src/components/features/medium/AssetsFilter/components/TimeWindow/TimeWindow.types";
import { useDebugMode } from "src/hooks/useDebugMode";
import { useSearchStorage } from "src/storage/useSearchStorage.ts";

export type CommonParams = {
  env_name?: string;
  app_pit?: string;
  task_id?: string | number;
  test_id?: string | number;
  tf_id?: string;
  ds_name?: string;
  app_name?: string;
  asset_name?: string;
  task_name?: string;
  /** Path segment for `GET .../tasks/:task_name/params/:param_name` */
  param_name?: string;
  cluster_name?: string;
  timeWindow?: TimeWindow;
  appOwners?: number[];
  appTags?: number[];
  is_debug_mode?: boolean;
};

type CommonParamsUtil = Omit<
  CommonParams,
  "timeWindow" | "appOwners" | "appTags" | "is_debug_mode"
> & { [key: string]: string };

export const useCommonQueryParams = <T extends (keyof CommonParams)[]>(
  keys?: T
): Pick<CommonParams, T[number]> => {
  const params = useParams<CommonParamsUtil>();
  const { timeWindow, appOwners, appTags } = useSearchStorage();
  const { showUsersFilter, showTagsFilter } = useShowFilters();
  const isDebugMode = useDebugMode();

  const { cluster_name, ...restParams } = params;

  // map "cluster_name" to "task_name"
  const commonParams = {
    ...restParams,
    ...(cluster_name && { task_name: cluster_name }),
    timeWindow,
    // only include `appTags` & `appOwners` if the filters are shown and the arrays are not empty
    ...(showUsersFilter && !!appOwners?.length && { appOwners }),
    ...(showTagsFilter && !!appTags?.length && { appTags }),
    ...(isDebugMode && { is_debug_mode: isDebugMode }),
  };

  if (keys !== undefined) {
    // Empty array means "return no params"
    if (keys.length === 0) {
      return {} as Pick<CommonParams, T[number]>;
    }

    // if "cluster_name" is in keys, replace it with "task_name"
    const transformedKeys = keys.includes("cluster_name")
      ? ([...keys.filter((k) => k !== "cluster_name"), "task_name"] as T)
      : keys;

    return pick(commonParams as CommonParams, transformedKeys) as Pick<
      CommonParams,
      T[number]
    >;
  }

  // No keys argument means "return all params"
  return commonParams as Pick<CommonParams, T[number]>;
};

/**
 * Merges custom body parameters with default parameters, filtering out undefined values.
 * This allows custom parameters to override defaults, but only when explicitly set.
 * If a custom parameter is `undefined`, the default value will be used instead.
 *
 * @param defaults - Default parameter values (e.g., from useCommonQueryParams)
 * @param custom - Custom parameter values that may override defaults
 * @returns Merged object where custom non-undefined values take precedence
 *
 * @example
 * const defaults = { env_name: 'prod', app_id: 123 };
 * const custom = { env_name: undefined, app_id: 456 };
 * mergeBodyWithDefaults(defaults, custom);
 * Returns: { env_name: 'prod', app_id: 456 }
 * Note: env_name uses default because custom value is undefined
 */
export const mergeBodyWithDefaults = <T extends Record<string, unknown>>(
  defaults: T,
  custom?: Partial<T> | Record<string, unknown>
): T =>
  ({
    ...defaults,
    ...Object.fromEntries(
      Object.entries(custom ?? {}).filter(([_, v]) => v !== undefined)
    ),
  }) as T;
